/**
 * Recovery for stale-chunk failures after a webapp deploy.
 *
 * The webapp is built by Vite with content-hashed, lazily-imported route chunks
 * (see `navigation/router.tsx`). When a new version is deployed the chunk
 * filenames change and the old files are removed from the CDN. A browser tab
 * still running the previous build holds an `index.html` that points at the
 * now-deleted hashes, so the first lazy navigation throws
 * "Failed to fetch dynamically imported module". A hard reload pulls the fresh
 * `index.html` + new hashes and recovers transparently.
 *
 * This is expected deploy churn, not an application bug (it is what produced the
 * Sentry crash SMARTMIXIN-WEB-39), so we self-heal with a single guarded reload
 * instead of surfacing a crash.
 */

// Substrings of the various browser messages for a failed dynamic import.
const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite CSS preload helper
];

const RELOAD_TIMESTAMP_KEY = 'sm:chunkReloadAt';

// If a reload does not fix the failure within this window the deploy is
// genuinely broken (or the device is offline) — stop reloading and let the
// caller surface the error instead of looping.
const RELOAD_GUARD_WINDOW_MS = 10_000;

const messageOf = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as {message: unknown}).message);
  }
  return '';
};

/** True when `error` looks like a failed dynamic-import / chunk load. */
export const isChunkLoadError = (error: unknown): boolean => {
  const message = messageOf(error);
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const readLastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
  } catch {
    return 0;
  }
};

/**
 * True when a guarded reload was already attempted inside the guard window,
 * i.e. reloading again would loop. Pure -- `attemptChunkReload` consults the
 * same timestamp, but also writes it and reloads, so a caller that only needs
 * the *decision* (to render the right thing on the first frame) uses this.
 */
export const chunkReloadSpent = (): boolean =>
  Date.now() - readLastReloadAt() < RELOAD_GUARD_WINDOW_MS;

/**
 * Attempt a single guarded hard reload to recover from a stale chunk.
 *
 * Returns `true` if a reload was triggered (the caller should stop rendering),
 * or `false` if a reload was already attempted within the guard window and
 * still failed (the caller should surface the error instead of looping).
 */
export const attemptChunkReload = (): boolean => {
  const now = Date.now();
  if (now - readLastReloadAt() < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }
  try {
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
  } catch {
    // sessionStorage unavailable (e.g. private mode) — reload once anyway.
  }
  window.location.reload();
  return true;
};

// Stable reference so repeated registration is deduped by addEventListener.
const handlePreloadError = (event: Event): void => {
  // Recover instead of letting Vite throw the unhandled preload error.
  event.preventDefault();
  attemptChunkReload();
};

/**
 * Listen for Vite's `vite:preloadError`, dispatched when a preloaded chunk
 * fails to load outside the router's lazy-import path. Registering the same
 * function reference twice is a no-op.
 */
export const registerChunkReloadListener = (): void => {
  window.addEventListener('vite:preloadError', handlePreloadError);
};
